import { isoToCalendarDayKey, rangeIncludesReservedDay } from './assetBookingCalendar';
import type { AssetBookingAddonOption } from './bookingAssetAddons';
import {
  validateBookingAddonQuantities,
  validateBookingAddonsInCatalog,
} from './bookingAssetAddons';
import type { BookingPeriodKind } from './assetBookingPricingOptions';
import {
  validateBookingCreateSchedule,
  validateBookingUpdatePeriodSchedule,
} from './bookingScheduleDefaults';

export type BookingCreateOrderValidateParams = {
  startIso: string;
  endIso: string;
  createAssetId: string | null;
  totalPrice: unknown;
  addonRows: Array<{ addon_id: number; quantity: number }>;
  addonChoices: AssetBookingAddonOption[];
  addonsCatalog: Record<string, unknown>[];
  hourly: boolean;
  sameDayHourly: boolean;
  periodKind: BookingPeriodKind | null | undefined;
  assetReservedDayKeys: Set<string>;
  couldNotCalculateTotalMessage: string;
  rangeBlockedMessage: string;
};

/**
 * Client-side booking order checks before payment or create.
 * Uses data already loaded for the form — no extra API calls.
 */
export function validateBookingCreateOrderReady(
  params: BookingCreateOrderValidateParams,
): string | null {
  const startIso = params.startIso.trim();
  const endIso = params.endIso.trim();

  const scheduleErr = validateBookingCreateSchedule(startIso, endIso, {
    hourly: params.hourly,
    sameDayHourly: params.sameDayHourly,
  });
  if (scheduleErr) {
    return scheduleErr;
  }

  if (params.periodKind) {
    const periodErr = validateBookingUpdatePeriodSchedule(startIso, endIso, params.periodKind, 1);
    if (periodErr) {
      return periodErr;
    }
  }

  const startDay = isoToCalendarDayKey(startIso);
  const endDay = isoToCalendarDayKey(endIso);
  if (params.sameDayHourly) {
    if (startDay && params.assetReservedDayKeys.has(startDay)) {
      return params.rangeBlockedMessage;
    }
  } else if (
    startDay &&
    endDay &&
    rangeIncludesReservedDay(startDay, endDay, params.assetReservedDayKeys)
  ) {
    return params.rangeBlockedMessage;
  }

  if (!params.createAssetId) {
    return 'Choose an asset before booking.';
  }

  if (typeof params.totalPrice !== 'number' || !Number.isFinite(params.totalPrice)) {
    return params.couldNotCalculateTotalMessage;
  }

  if (params.addonRows.length > 0) {
    const catalogErr = validateBookingAddonsInCatalog(
      params.addonRows,
      params.addonsCatalog,
      params.addonChoices,
    );
    if (catalogErr) {
      return catalogErr;
    }
    if (params.addonChoices.length > 0) {
      const addonErr = validateBookingAddonQuantities(
        params.addonRows,
        params.addonChoices,
        params.addonsCatalog,
      );
      if (addonErr) {
        return addonErr;
      }
    }
  }

  return null;
}
